import { useState, useEffect, useRef } from "react";
import { Menu } from "antd";
import { useAuth } from "./auth";
import "./SideMenu.css";
import { Link, useLocation } from "react-router-dom";
import {
  User,
  BarChart2,
  ClipboardCheck,
  List,
  Users,
  Building2,
  CreditCard,
  FileText,
  Bell,
  Calendar,
  Calculator,
  UserCheck,
  History,
} from "lucide-react";

const createItem = (key, label, icon, children) => {
  return { key, label, icon, children };
};

const createLinkItem = (key, label, icon, to) => {
  const item = createItem(
    key,
    <Link to={to}>
      <span className="side-menu-item">{label}</span>
    </Link>,
    <span className="side-menu-item-icon">{icon}</span>
  );
  item.to = to;
  return item;
};

// Recursive function to find all menu items with their paths
const getAllMenuItems = (items) => {
  const allItems = [];

  const traverse = (itemList) => {
    itemList.forEach((item) => {
      if (item.to) {
        allItems.push({ key: item.key, path: item.to });
      }
      if (item.children) {
        traverse(item.children);
      }
    });
  };

  traverse(items);
  return allItems;
};

const getSideMenuItems = (role) => {
  const iconProps = { size: 18, strokeWidth: 1.5 };

  const baseItems = [
    createLinkItem("0", "Perfil", <User {...iconProps} />, "/perfil"),
    createLinkItem("1", "Estadísticas", <BarChart2 {...iconProps} />, "/"),
    createLinkItem("2", "Verificaciones", <ClipboardCheck {...iconProps} />, "/verificaciones"),
  ];

  const listasItem = createItem(
    "7",
    <span className="side-menu-item">Listas</span>,
    <span className="side-menu-item-icon">
      {" "}
      <List {...iconProps} />{" "}
    </span>,
    [
      createLinkItem("6-2", "Clientes", <Users {...iconProps} />, "/clientes"),
      createLinkItem("6-3", "Razones sociales", <Building2 {...iconProps} />, "/razonessociales"),
      createLinkItem("6-4", "Personas", <User {...iconProps} />, "/personas"),
    ]
  );

  const listasItemAdmin = createItem(
    "7",
    <span className="side-menu-item">Listas</span>,
    <span className="side-menu-item-icon">
      {" "}
      <List {...iconProps} />{" "}
    </span>,
    [
      createLinkItem("6-3", "Razones sociales", <Building2 {...iconProps} />, "/razonessociales"),
      createLinkItem("6-4", "Personas", <User {...iconProps} />, "/personas"),
    ]
  );

  const pagosSubMenu = createItem(
    "9",
    <span className="side-menu-item">Pagos</span>,
    <span className="side-menu-item-icon">
      {" "}
      <CreditCard {...iconProps} />{" "}
    </span>,
    [
      createLinkItem("9-1", "Por Verificacion", <FileText {...iconProps} />, "/pagos/por-verificacion"),
      createLinkItem("9-2", "Historial", <History {...iconProps} />, "/pagos"),
    ]
  );

  if (role === "administrador") {
    const filteredBaseItems = baseItems.filter((item) => item.key !== "0");
    return [...filteredBaseItems, pagosSubMenu, listasItemAdmin, createLinkItem("10", "Reportes", <FileText {...iconProps} />, "/reportes"),];
  } else if (role === "director") {
    return [
      ...baseItems,
      createLinkItem("3", "Notificaciones", <Bell {...iconProps} />, "/notificaciones"),
      createLinkItem("4", "Calendario", <Calendar {...iconProps} />, "/calendario"),
      createLinkItem("5", "Cotizaciones", <Calculator {...iconProps} />, "/cotizaciones"),
      createLinkItem("6", "Usuarios", <Users {...iconProps} />, "/usuarios"),
      createLinkItem("8", "Verificadores", <UserCheck {...iconProps} />, "/verificadores"),
      createLinkItem("10", "Reportes", <FileText {...iconProps} />, "/reportes"),
      pagosSubMenu,
      listasItem,
    ];
  } else if (role === "verificador") {
    return [
      ...baseItems,
      createLinkItem("3", "Notificaciones", <Bell {...iconProps} />, "/notificaciones"),
      createLinkItem("4", "Calendario", <Calendar {...iconProps} />, "/calendario"),
      createLinkItem("5", "Cotizaciones", <Calculator {...iconProps} />, "/cotizaciones"),
      createLinkItem("8", "Verificadores", <UserCheck {...iconProps} />, "/verificadores"),
      listasItem,
    ];
  } else if (role === "payment") {
    return [
      createLinkItem("0", "Perfil", <User {...iconProps} />, "/perfil"),
      createLinkItem("2", "Verificaciones", <ClipboardCheck {...iconProps} />, "/verificaciones"),
      pagosSubMenu,
      listasItem,
    ];
  }

  return [...baseItems, listasItem];
};

export default function SideMenu({ collapsed }) {
  const [currentLocation, setCurrentLocation] = useState();
  const [isAnimated, setIsAnimated] = useState(false);
  const containerRef = useRef(null);
  const location = useLocation();
  const auth = useAuth();
  const items = getSideMenuItems(auth?.Rol);

  // Handle staggered animations
  useEffect(() => {
    if (!containerRef.current || isAnimated) return;

    // Small delay to ensure Menu is fully rendered
    const timeoutId = setTimeout(() => {
      const menuItems = containerRef.current.querySelectorAll(".ant-menu-item, .ant-menu-submenu > .ant-menu-submenu-title");

      if (menuItems.length === 0) return;

      // Set initial state
      menuItems.forEach((item) => {
        item.style.opacity = "0";
        item.style.transform = "translateX(-10px)";
        item.style.transition = "opacity 0.3s ease-out, transform 0.3s ease-out";
      });

      // Stagger the animations
      menuItems.forEach((item, index) => {
        setTimeout(() => {
          item.style.opacity = "1";
          item.style.transform = "translateX(0)";
        }, index * 50 + 100);
      });

      setIsAnimated(true);
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [items, isAnimated]);

  // Reset animation when items change (role change, etc.)
  useEffect(() => {
    setIsAnimated(false);
  }, [items]);

  useEffect(() => {
    const allMenuItems = getAllMenuItems(items);
    const currentPath = location.pathname;

    const exactMatch = allMenuItems.find((item) => item.path === currentPath);

    if (exactMatch) {
      setCurrentLocation(exactMatch.key);
    } else {
      const targetPath = location.pathname.split("/").slice(0, 2).join("/");
      const targetItem = items.find((item) => item.to === targetPath);
      setCurrentLocation(targetItem?.key);
    }
  }, [location, items]);

  return (
    <div className="side-menu-container" ref={containerRef}>
      <Menu mode="inline" theme="dark" items={items} selectedKeys={[currentLocation]} className="enhanced-side-menu js-animated" inlineCollapsed={collapsed} />
    </div>
  );
}